import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	IPlayerStatGroup,
	PlayersByTeamFilter,
	PlayerSortConfig,
} from "@data-types/sport-stats/SportStatsTypes";
import classNames from "classnames";
import LivePlayerStatisticsStore from "@state/stores/offer/statistics/LivePlayerStatisticsStore";
import CountryIcon from "@components/icons/CountryIcon";

export function PlayerStatisticsTable(props: {
	statsGroup: IPlayerStatGroup;
	store: LivePlayerStatisticsStore;
	index?: number;
	playerFilter: PlayersByTeamFilter;
	setPlayerFilter: (playerFilter: PlayersByTeamFilter) => void;
}) {
	const { t } = useTranslation();

	const [sortConfig, setSortConfig] = useState<PlayerSortConfig>({
		key: "position",
		direction: "asc",
	});

	const handleSort = (key: string) => {
		setSortConfig((prevSortConfig) => {
			const direction =
				prevSortConfig?.key === key &&
				prevSortConfig.direction === "asc"
					? "desc"
					: "asc";
			return { key, direction };
		});
	};

	const filteredPlayers = props.store.filterPlayers(
		props.playerFilter,
		props.statsGroup.key,
		sortConfig
	);

	const getIconClass = (key: string) =>
		classNames("table__sort__icon u-icon u-icon--tny", {
			"u-icon--caret--down u-color--sent--danger":
				sortConfig?.key === key && sortConfig?.direction === "desc",
			"u-icon--caret--up u-color--sent--danger":
				sortConfig?.key === key && sortConfig?.direction === "asc",
			"u-icon--caret--down u-color--sent--muted": sortConfig?.key !== key,
		});

	const getButtonClassName = (filter: string) =>
		classNames("btn btn--sml", {
			"btn--accent": props.playerFilter === filter,
			"btn--primary--inverted": props.playerFilter !== filter,
		});

	const baseCellClasses = classNames("table__cell table__cell--tny");

	const headCellClasses = classNames(baseCellClasses, "u-align--v--top");

	const bodyCellClasses = classNames(baseCellClasses, "u-align--v--middle");

	return (
		<div className="event__content">
			<div className="table--responsive scrollbar--thin">
				<table className="table u-type--xxsml">
					<thead className="table__head table__head--smoke table__head--bordered u-type--wgt--medium">
						<tr className="table__row">
							<th colSpan={2} className={baseCellClasses}>
								<div className="u-display--f u-gap--tny">
									<button
										type="button"
										className={getButtonClassName("all")}
										onClick={() =>
											props.store.setActiveTab("all")
										}
									>
										{t("EVENT.STATISTICS.PLAYER_STATS.ALL")}
									</button>
									<button
										type="button"
										className={getButtonClassName("home")}
										onClick={() =>
											props.store.setActiveTab("home")
										}
									>
										{t(
											"EVENT.STATISTICS.PLAYER_STATS.HOME"
										)}
									</button>
									<button
										type="button"
										className={getButtonClassName("away")}
										onClick={() =>
											props.store.setActiveTab("away")
										}
									>
										{t(
											"EVENT.STATISTICS.PLAYER_STATS.AWAY"
										)}
									</button>
								</div>
							</th>
							{props.statsGroup.key !== "goalkeeper" && (
								<th
									className={headCellClasses + " table__sort"}
									onClick={() => handleSort("position")}
								>
									<div>
										{t(
											"EVENT.STATISTICS.PLAYER_STATS.PLAYER_STAT_TITLES.POSITION"
										)}
									</div>
									<i className={getIconClass("position")} />
								</th>
							)}
							{props.statsGroup.data.map((stat) => (
								<th
									key={stat.key}
									className={headCellClasses + " table__sort"}
									onClick={() => handleSort(stat.key)}
								>
									<div>{t(stat.title)}</div>
									<i className={getIconClass(stat.key)} />
								</th>
							))}
							<th
								className={headCellClasses + " table__sort"}
								onClick={() => handleSort("rating")}
							>
								<div>
									{t(
										"EVENT.STATISTICS.PLAYER_STATS.PLAYER_STAT_TITLES.RATING"
									)}
								</div>
								<i className={getIconClass("rating")} />
							</th>
						</tr>
					</thead>

					<tbody className="table__body">
						{filteredPlayers.map((player, index) => {
							const playerRating = +player.rating.toFixed(1);

							const counterClassName = classNames(
								"tag tag--base tag--rating",
								{
									"tag--rating--bottom":
										playerRating > 0 && playerRating <= 6,
									"tag--rating--low":
										playerRating > 6 && playerRating <= 6.5,
									"tag--rating--medium":
										playerRating > 6.5 && playerRating <= 7,
									"tag--rating--good":
										playerRating > 7 && playerRating <= 8,
									"tag--rating--high":
										playerRating > 8 && playerRating <= 9,
									"tag--rating--top":
										playerRating > 9 && playerRating <= 10,
									"tag--rating--none": !player.rating,
								},
								"u-align--v--middle"
							);

							return (
								<tr
									className="table__row"
									key={player.targetId || index}
								>
									<td
										className={
											bodyCellClasses + " u-type--center"
										}
									>
										<CountryIcon
											flagCode={player.country}
											className="u-icon--base u-align--v--top"
										/>
									</td>
									<td
										className={
											bodyCellClasses +
											" u-type--left u-type--wgt--medium u-color--text--primary"
										}
									>
										{player.name}
									</td>
									{props.statsGroup.key !== "goalkeeper" && (
										<td
											className={
												bodyCellClasses +
												" u-type--center"
											}
										>
											{player.position}
										</td>
									)}
									{props.statsGroup.data.map((stat) => {
										return (
											<td
												key={stat.key}
												className={
													bodyCellClasses +
													" u-type--center"
												}
											>
												{
													player.statistics[
														props.statsGroup.key
													]?.[stat.key]
												}
											</td>
										);
									})}
									<td
										className={
											bodyCellClasses + " u-type--center"
										}
									>
										<span className={counterClassName}>
											{player.rating
												? player.rating.toFixed(1)
												: "-"}
										</span>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
